import React, { useEffect, useState } from 'react';
import { useRecordContext, useDataProvider } from 'react-admin';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';

const AITab = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const [usageData, setUsageData] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!record?.id) return;

        dataProvider
            .getList('aiusage', {
                pagination: { page: 1, perPage: 2000 },
                sort: { field: 'date', order: 'DESC' },
                filter: { iduser: record.id },
            })
            .then(({ data }) => {
                setUsageData(data);

                const monthMap = {};
                data.forEach((item) => {
                    if (!item.date) return;
                    const d = new Date(item.date);
                    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                    if (!monthMap[key]) {
                        monthMap[key] = { month: key, input_tokens: 0, output_tokens: 0 };
                    }
                    monthMap[key].input_tokens += item.input_tokens || 0;
                    monthMap[key].output_tokens += item.output_tokens || 0;
                });

                const sorted = Object.values(monthMap).sort((a, b) =>
                    a.month.localeCompare(b.month)
                );
                setChartData(sorted);
            })
            .catch(() => {
                setUsageData([]);
                setChartData([]);
            })
            .finally(() => setLoading(false));
    }, [dataProvider, record?.id]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!usageData.length) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                    🤖 Uso de AI
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Este usuario no tiene registros de consumo de AI.
                </Typography>
            </Box>
        );
    }

    const totalInput = usageData.reduce((s, r) => s + (r.input_tokens || 0), 0);
    const totalOutput = usageData.reduce((s, r) => s + (r.output_tokens || 0), 0);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Chip
                    label={`Total registros: ${usageData.length}`}
                    color="primary"
                    variant="outlined"
                />
                <Chip
                    label={`Tokens entrada: ${totalInput.toLocaleString()}`}
                    color="info"
                    variant="outlined"
                />
                <Chip
                    label={`Tokens salida: ${totalOutput.toLocaleString()}`}
                    color="secondary"
                    variant="outlined"
                />
                <Chip
                    label={`Total tokens: ${(totalInput + totalOutput).toLocaleString()}`}
                    color="success"
                    variant="outlined"
                />
            </Box>

            {chartData.length > 0 && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                            📊 Consumo Mensual de Tokens
                        </Typography>
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                <YAxis
                                    tick={{ fontSize: 12 }}
                                    tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                                />
                                <Tooltip
                                    formatter={(value, name) => [
                                        value.toLocaleString(),
                                        name === 'input_tokens' ? 'Tokens Entrada' : 'Tokens Salida',
                                    ]}
                                    labelFormatter={(label) => `Mes: ${label}`}
                                />
                                <Legend
                                    formatter={(value) =>
                                        value === 'input_tokens' ? 'Tokens Entrada' : 'Tokens Salida'
                                    }
                                />
                                <Bar dataKey="input_tokens" stackId="a" fill="#1976d2" />
                                <Bar dataKey="output_tokens" stackId="a" fill="#42a5f5" />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                        🗂️ Detalle de Uso
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Opts / URL</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Entrada</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Salida</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Total</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Ver</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {usageData.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        hover
                                        sx={{ '&:last-child td': { border: 0 } }}
                                    >
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                                            {row.date
                                                ? new Date(row.date).toLocaleString('es-ES', {
                                                      year: 'numeric',
                                                      month: '2-digit',
                                                      day: '2-digit',
                                                      hour: '2-digit',
                                                      minute: '2-digit',
                                                  })
                                                : '-'}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                maxWidth: 320,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                fontSize: '0.8rem',
                                            }}
                                            title={row.opts || row.description || '-'}
                                        >
                                            {row.opts || row.description || '-'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                                            {(row.input_tokens || 0).toLocaleString()}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                                            {(row.output_tokens || 0).toLocaleString()}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                                            {((row.input_tokens || 0) + (row.output_tokens || 0)).toLocaleString()}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Button
                                                component={Link}
                                                to={`/aiusage/${row.id}/show`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                Ver
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
};

export default AITab;